import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, User, LogOut, Bell, User2 } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { client, getFullName } from '../../apiHelper';


const AvatarPlaceholder = () => (
    <div className="w-10 h-10 rounded-full bg-gray-dark flex items-center justify-center text-white text-lg font-bold">
        <User size={20} />
    </div>
);

const Header = ({ onMenuToggle }) => {
    const navigate = useNavigate();
    const [showDropdown, setShowDropdown] = useState(false);
    const [user, setUser] = useState(null);
    const { showToast } = useToast();
    const [notifications, setNotifications] = useState([]);
    const [showNotifs, setShowNotifs] = useState(false);
    const userMenuRef = useRef(null);
    const notifRef = useRef(null);

    useEffect(() => {
        const fetchNotifs = async () => {
            try {
                const res = await client.fetchData('/notification/unread');
                setNotifications(res || []);
            } catch (err) {
                if (err.response?.data?.detail === "Could not validate credentials.") {
                    navigate('/login');
                }
                console.error("Failed to load notifications", err);
            }
        };

        fetchNotifs();
        const interval = setInterval(fetchNotifs, 7200000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            console.warn("User not found in local storage. Please log in.");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // User dropdown
            if (showDropdown && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowDropdown(false);
            }

            // Notifications panel
            if (showNotifs && notifRef.current && !notifRef.current.contains(event.target)) {
                setShowNotifs(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, showNotifs]);

    const handleLogout = async () => {
        try {
            showToast("Logging out", "info");
            localStorage.clear();
            setShowDropdown(false);
            const response = await client.postData('/auth/logout', user);
            showToast(response.msg || 'Good bye!', 'info');
            setTimeout(() => {
                navigate('/login');
            }, 300);
        } catch (err) {
            console.error(err?.response?.data?.detail || 'Unexpected error contact admin.');
        }
    };

    const fullName = getFullName(user);

    return (
        <header className="flex items-center justify-between p-4 bg-white shadow-sm sticky top-0 z-40 border-b border-gray-medium">
            {/* Left section: Menu Toggle and App Name */}
            <div className="flex items-center gap-4">
                <button
                    type="button"
                    onClick={onMenuToggle}
                    className="p-2 rounded-lg text-text-secondary hover:bg-gray-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={24} />
                </button>
                <div className="flex items-baseline space-x-1">
                    <h2 className="text-2xl font-bold text-text-primary">Arxis</h2>
                    <h2 className="text-[10px] align-sub uppercase tracking-wide text-text-primary">accounting</h2>
                </div>
            </div>

            {/* Right section: Search and User Profile */}
            <div className="flex items-center gap-4">
                <div className="relative flex items-center">
                    {/* <input
                        type="text"
                        placeholder="Search..."
                        className="pl-10 pr-4 py-2 border border-gray-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue transition-all duration-200 ease-in-out w-full max-w-xs md:max-w-none"
                        aria-label="Search"
                    />
                    <Search size={18} className="absolute left-3 text-gray-dark" /> */}
                </div>

                {/* Notifications Bell */}
                <div className="relative" ref={notifRef}>
                    <button
                        onClick={() => setShowNotifs(!showNotifs)}
                        className="relative p-2 rounded-lg hover:bg-gray-light focus:outline-none focus:ring-2 focus:ring-gmail-blue"
                    >
                        <Bell size={22} />
                        {notifications.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                {notifications.length}
                            </span>
                        )}
                    </button>

                    {showNotifs && (
                        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-medium z-50 max-h-96 overflow-hidden flex flex-col">
                            <div className="p-3 border-b border-gray-medium font-semibold text-text-primary">
                                Notifications ({notifications.length})
                            </div>

                            <div className="overflow-y-auto flex-1">
                                {notifications.length === 0 ? (
                                    <p className="p-4 text-center text-text-secondary">No new notifications</p>
                                ) : (
                                    notifications.map((notif) => (
                                        <div
                                            key={notif.id}
                                            className="p-4 border-b border-gray-light hover:bg-gray-50"
                                        >
                                            <p className="font-medium text-sm text-text-primary">{notif.title}</p>
                                            <p className="text-xs text-text-secondary mt-1">{notif.message}</p>

                                            {/* Display low stock items if present */}
                                            {notif.notification_metadata?.type === 'low_stock' &&
                                                notif.notification_metadata?.items?.length > 0 && (
                                                    <div className="mt-3 pt-2 border-t border-gray-200">
                                                        <p className="text-xs font-semibold text-orange-600 mb-2">
                                                            Affected items ({notif.notification_metadata.items.length}):
                                                        </p>
                                                        <ul className="space-y-1 max-h-48 overflow-y-auto">
                                                            {notif.notification_metadata.items.map((item, idx) => (
                                                                <li key={idx} className="text-xs font-bold text-text-primary">
                                                                    {item.product_name}
                                                                    <span className="font-normal text-text-secondary ml-2">
                                                                        ({item.remaining} left in {item.store})
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                            {/* Optional: for other metadata types, e.g., expiring items */}
                                            {notif.notification_metadata?.expiring_items && (
                                                <p className="text-xs text-orange-600 mt-2">
                                                    {notif.notification_metadata.expiring_items.length} item(s) expiring soon
                                                </p>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* User Dropdown */}
                <div className="relative flex items-center gap-3" ref={userMenuRef}>
                    <div className="hidden md:flex flex-col items-end">
                        <span className="text-sm font-medium text-text-primary">
                            {fullName}
                        </span>
                        {/* <span className="text-xs text-text-secondary">
                            {user?.role || ''}
                        </span> */}
                    </div>
                    <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="w-10 h-10 rounded-full overflow-hidden border-2 border-transparent hover:border-gmail-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue transition-all flex items-center justify-center shrink-0"
                        aria-label="User menu"
                    >
                        {/* <AvatarPlaceholder /> */}
                        {user?.first_name ? (
                            <div className="w-10 h-10 rounded-full bg-gmail-blue text-white flex items-center justify-center font-semibold text-lg uppercase">
                                {(user.first_name[0] || '') + (user.last_name?.[0] || '')}
                            </div>
                        ) : (
                            <AvatarPlaceholder />
                        )}
                    </button>

                    {showDropdown && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg z-50 border border-gray-medium py-1">
                            <div className="px-4 py-3 border-b border-gray-100">
                                {/* <p className="text-sm font-medium text-text-primary">{fullName}</p>
                                <p className="text-xs text-text-secondary">{user?.logon_id || '-'}</p> */}
                            </div>
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;